"use client";

import { useEffect, useState } from "react";
import { axiosClient } from "@/lib/axiosClient";
import {
    Loader2,
    Search,
    ChevronLeft,
    ChevronRight,
    Users,
    RefreshCcw,
    ArrowUpDown,
    Edit,
    Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import {
    // Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ParticipantEditModal from "@/components/private/pic/ParticipantEditModal";
// import { ScrollArea } from "@/components/ui/scroll-area";

interface ParticipantItem {
    participant_id: number;
    full_name: string;
    email: string;
    institution: string | null;
    phone_mobile: string | null;
    gender: string | null;
    blood_type: string | null;
    clothing_size: string | null;
    registration?: {
        registration_number: string;
        payment_status: string;
        package_name: string;
        price: number;
        package_id?: number;
    } | null;
}

interface ApiResponse {
    message: string;
    data: ParticipantItem[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

interface ParticipantForm {
    participant_id: number;
    full_name: string;
    email: string;
    phone_mobile: string | null;
    gender: string | null;
    blood_type: string | null;
    clothing_size: string | null;
    institution: string | null;
    package_id: number | null;
}

interface ParticipantTableProps {
    reloadTrigger?: number;
    onDataLoaded?: (count: number) => void;
}

export default function ParticipantTable({ reloadTrigger, onDataLoaded }: ParticipantTableProps) {
    const [data, setData] = useState<ParticipantItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<string>("full_name");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    const [showEdit, setShowEdit] = useState(false);
    const [selectedParticipantForm, setSelectedParticipantForm] = useState<ParticipantForm | null>(null);

    // 🕐 debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);

    const fetchData = async (): Promise<void> => {
        try {
            setLoading(true);
            const { data } = await axiosClient.get<ApiResponse>("/pic/participants", {
                params: {
                    search: debouncedSearch,
                    page,
                    per_page: perPage,
                    sort_by: sortField,
                    sort_order: sortOrder,
                },
            });
            setData(data.data);
            setTotalPage(data.meta.last_page);
            setTotal(data.meta.total);

            if (onDataLoaded) {
                onDataLoaded(data.meta.total);
            }
        } catch (err) {
            console.error(err);
            toast.error("Gagal memuat daftar peserta.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchData();
    }, [page, debouncedSearch, perPage, reloadTrigger, sortField, sortOrder]);

    const toggleSort = (field: string) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const openEditModal = (item: ParticipantItem) => {
        const mapped: ParticipantForm = {
            participant_id: item.participant_id,
            full_name: item.full_name,
            email: item.email,
            phone_mobile: item.phone_mobile ?? null,
            gender: item.gender ?? null,
            blood_type: item.blood_type ?? null,
            clothing_size: item.clothing_size ?? null,
            institution: item.institution ?? null,
            package_id: item.registration?.package_id ?? null,
        };
        setSelectedParticipantForm(mapped);
        setShowEdit(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await axiosClient.delete(`/pic/participants/${id}`);
            // toast.success("Peserta berhasil dihapus.");
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error("Gagal menghapus peserta.");
        }
    };

    const start = total > 0 ? (page - 1) * perPage + 1 : 0;
    const end = Math.min(page * perPage, total);

    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [openDialogId, setOpenDialogId] = useState<number | null>(null);

    return (
        <Card className="border-0 shadow-md bg-white dark:bg-gray-900">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-2">
                <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <CardTitle className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        Daftar Peserta Kolektif
                    </CardTitle>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Cari peserta..."
                            className="pl-9"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => fetchData()}
                        disabled={loading}
                        title="Muat ulang"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCcw className="w-4 h-4" />
                        )}
                    </Button>
                </div>
            </CardHeader>

            <CardContent className="relative">
                {/* Overlay loading blur */}
                {loading && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mb-2" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Memuat data peserta...
                        </p>
                    </div>
                )}

                {/* Konten tabel tetap dirender agar ukuran stabil */}
                <div className="w-full overflow-x-auto rounded-lg border">
                    <table className="w-full min-w-max table-auto">
                        <TableHeader>
                            <TableRow className="bg-gray-50 dark:bg-gray-800">
                                {[
                                    { key: "full_name", label: "Nama" },
                                    { key: "email", label: "Email" },
                                    { key: "institution", label: "Institusi" },
                                    { key: "package", label: "Paket & Harga" },
                                    { key: "status", label: "Status" },
                                    { key: "actions", label: "Aksi" },
                                ].map((col) => (
                                    <TableHead
                                        key={col.key}
                                        onClick={() =>
                                            ["full_name", "email", "institution"].includes(col.key)
                                                ? toggleSort(col.key)
                                                : undefined
                                        }
                                        className={`cursor-pointer select-none ${["full_name", "email", "institution"].includes(col.key)
                                                ? "hover:text-blue-600"
                                                : ""
                                            }`}
                                    >
                                        <div className="flex items-center gap-1">
                                            {col.label}
                                            {["full_name", "email", "institution"].includes(col.key) && (
                                                <ArrowUpDown className="w-3.5 h-3.5 opacity-60" />
                                            )}
                                        </div>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {data.length > 0 ? (
                                data.map((item) => (
                                    <TableRow
                                        key={item.participant_id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                                    >
                                        <TableCell>
                                            <div className="font-medium">{item.full_name}</div>
                                            <div className="text-sm text-gray-500">
                                                {item.registration?.registration_number || "-"}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div>{item.email}</div>
                                            <div className="text-sm text-gray-500">{item.phone_mobile}</div>
                                        </TableCell>
                                        <TableCell>{item.institution || "-"}</TableCell>
                                        <TableCell>
                                            {item.registration ? (
                                                <>
                                                    <div>{item.registration.package_name}</div>
                                                    <div className="text-sm text-gray-500">
                                                        Rp {item.registration.price.toLocaleString("id-ID")}
                                                    </div>
                                                </>
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {item.registration?.payment_status === "paid" ? (
                                                <span className="text-green-600 font-medium">Lunas</span>
                                            ) : (
                                                <span className="text-yellow-600 font-medium">
                                                    Belum Bayar
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="flex justify-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEditModal(item)}
                                                title="Edit Peserta"
                                                className="hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-white"
                                            >
                                                <Edit className="w-4 h-4 text-blue-600" />
                                            </Button>

                                            <AlertDialog
                                                open={openDialogId === item.participant_id}
                                                onOpenChange={(open) => setOpenDialogId(open ? item.participant_id : null)}
                                            >
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        title="Hapus Peserta"
                                                        className="hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-white"
                                                        onClick={() => setOpenDialogId(item.participant_id)}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                    </Button>
                                                </AlertDialogTrigger>

                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Hapus Peserta?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Tindakan ini akan menghapus data peserta{" "}
                                                            <strong>{item.full_name}</strong>. Data yang sudah
                                                            memiliki tagihan aktif tidak dapat dihapus.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>

                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel disabled={deletingId === item.participant_id}>
                                                            Batal
                                                        </AlertDialogCancel>

                                                        {/* Gunakan Button manual agar alert tidak auto-close */}
                                                        <Button
                                                            variant="destructive"
                                                            disabled={deletingId === item.participant_id}
                                                            onClick={async () => {
                                                                setDeletingId(item.participant_id);
                                                                try {
                                                                    // ⏳ Proses hapus data ke server
                                                                    await handleDelete(item.participant_id);

                                                                    // ✅ Setelah hapus berhasil, tutup alert dulu
                                                                    setOpenDialogId(null);

                                                                    // ⏱️ Setelah alert tertutup (beri jeda kecil biar smooth)
                                                                    setTimeout(() => {
                                                                        toast.success("Peserta berhasil dihapus.");
                                                                        fetchData(); // reload table
                                                                    }, 400);
                                                                } catch (err) {
                                                                    console.log(err);
                                                                    toast.error("Gagal menghapus peserta.");
                                                                } finally {
                                                                    setDeletingId(null);
                                                                }
                                                            }}
                                                        >
                                                            {deletingId === item.participant_id ? (
                                                                <>
                                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                    Menghapus...
                                                                </>
                                                            ) : (
                                                                "Hapus"
                                                            )}
                                                        </Button>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>


                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-gray-500 py-5">
                                        Tidak ada peserta ditemukan.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </table>
                </div>

                {/* Modal Edit */}
                <ParticipantEditModal
                    open={showEdit}
                    onClose={() => {
                        setShowEdit(false);
                        setSelectedParticipantForm(null);
                    }}
                    participant={selectedParticipantForm}
                    onSuccess={fetchData}
                />

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-5 text-sm text-gray-600 dark:text-gray-300 w-full">
                    <div>
                        Menampilkan <strong>{start}</strong>–<strong>{end}</strong> dari{" "}
                        <strong>{total}</strong> peserta
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span>Tampilkan</span>
                            <Select
                                value={perPage.toString()}
                                onValueChange={(val) => {
                                    setPerPage(Number(val));
                                    setPage(1);
                                }}
                            >
                                <SelectTrigger className="w-[80px] h-8 text-sm">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[10, 25, 50, 100].map((num) => (
                                        <SelectItem key={num} value={num.toString()}>
                                            {num}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <span>entri per halaman</span>
                        </div>

                        {totalPage > 1 && (
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page === 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                                </Button>
                                <span>
                                    Halaman {page} dari {totalPage}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page === totalPage}
                                    onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                                >
                                    Next <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
